import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { MapPin, Phone, Navigation, CheckCircle, XCircle } from 'lucide-react';
import { servicesAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const ServiceTracking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { service, serviceRequestId, estimatedCost } = location.state || {};
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!serviceRequestId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const data = await servicesAPI.getServiceTracking(serviceRequestId);
        if (!cancelled) setTracking(data);
      } catch (error) {
        console.error('Error fetching tracking info:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    pollRef.current = poll;
    poll();
    const interval = setInterval(poll, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [serviceRequestId]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const result = await servicesAPI.cancelServiceRequest(serviceRequestId, cancelReason);
      toast({ title: 'Cancellation sent', description: result.message });
      setShowCancel(false);
      if (pollRef.current) await pollRef.current();
    } catch (error) {
      toast({
        title: "Couldn't cancel",
        description: error.response?.data?.detail || 'Please try again, or email hello@wheelyrescue.co.uk',
        variant: 'destructive'
      });
    } finally {
      setCancelling(false);
    }
  };

  const handlePay = () => {
    navigate('/customer/payment', {
      state: {
        service,
        serviceRequestId,
        estimatedCost
      }
    });
  };

  if (!service || !serviceRequestId) {
    navigate('/customer/dashboard');
    return null;
  }

  const status = tracking?.status;
  const driver = tracking?.driver;
  const etaMinutes = tracking?.eta_minutes;
  const requestDetails = tracking?.service_request;
  const paid = requestDetails?.payment_status === 'completed';
  const cancellationRequested = !!requestDetails?.cancellation_requested_at;
  const canCancel = !loading && !cancellationRequested && ['pending', 'quoted', 'accepted', 'in_progress'].includes(status);
  const initials = driver?.name
    ? driver.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <div className="min-h-screen bg-wheely-navy-950">
      {/* Map Mock */}
      <div className="relative h-96 bg-gradient-to-br from-wheely-navy-900 via-zinc-800 to-wheely-navy-900 border-b border-wheely-orange/20">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="relative">
              <div className="w-32 h-32 bg-wheely-orange/20 rounded-full animate-ping absolute"></div>
              <div className="w-32 h-32 bg-wheely-orange/40 rounded-full flex items-center justify-center relative">
                <MapPin className="w-16 h-16 text-wheely-orange" />
              </div>
            </div>
            <p className="text-white text-xl font-semibold">Your Location</p>
          </div>
        </div>

        {driver && (
          <div className="absolute top-1/4 right-1/4">
            <div className="relative">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full animate-pulse absolute"></div>
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center relative">
                <Navigation className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status & Driver Info */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Status Card */}
        <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
          <CardContent className="pt-6">
            {loading && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 border-4 border-wheely-orange border-t-transparent rounded-full animate-spin mx-auto"></div>
                <h3 className="text-2xl font-bold text-white">Loading...</h3>
              </div>
            )}

            {!loading && status === 'cancelled' && (
              <div className="text-center space-y-4">
                <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white">Request Cancelled</h3>
                <p className="text-gray-400">
                  {requestDetails?.refund_amount > 0
                    ? `£${Number(requestDetails.refund_amount).toFixed(2)} has been refunded to your card.`
                    : paid ? 'Check your email for the details of your cancellation.' : "You haven't been charged."}
                </p>
              </div>
            )}

            {!loading && !driver && status !== 'cancelled' && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 border-4 border-wheely-orange border-t-transparent rounded-full animate-spin mx-auto"></div>
                <h3 className="text-2xl font-bold text-white">
                  {status === 'pending' || status === 'quoted' ? 'Waiting for a Driver to Accept...' : 'Finding Your Driver...'}
                </h3>
                <p className="text-gray-400">This usually takes 30-60 seconds</p>
              </div>
            )}

            {!loading && driver && status !== 'completed' && status !== 'cancelled' && (
              <div className="space-y-6">
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {status === 'in_progress' ? 'Driver Arriving Soon!' : 'Driver Found!'}
                  </h3>
                  <p className="text-wheely-orange font-semibold text-lg">
                    {etaMinutes != null ? `Arriving in ${etaMinutes} minute${etaMinutes === 1 ? '' : 's'}` : 'Calculating ETA...'}
                  </p>
                </div>

                <div className="flex items-center gap-4 p-4 bg-wheely-navy-900 rounded-lg">
                  <Avatar className="w-16 h-16 border-2 border-wheely-orange">
                    <AvatarFallback className="bg-wheely-orange text-white text-xl">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h4 className="text-white font-bold text-lg">{driver.name}</h4>
                    <p className="text-gray-400">Recovery Driver</p>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-wheely-orange">★★★★★</span>
                      <span className="text-gray-400 text-sm">({driver.rating})</span>
                    </div>
                  </div>
                  {driver.phone && driver.phone !== 'N/A' && (
                    <Button
                      size="icon"
                      className="bg-green-600 hover:bg-green-700 rounded-full w-12 h-12"
                      onClick={() => window.location.href = `tel:${driver.phone}`}
                    >
                      <Phone className="w-5 h-5" />
                    </Button>
                  )}
                </div>

                <div className="bg-wheely-navy-900 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Vehicle</span>
                    <span className="text-white font-semibold">{driver.vehicle_model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">License Plate</span>
                    <span className="text-white font-semibold">{driver.license_plate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Service</span>
                    <span className="text-white font-semibold">{service.name}</span>
                  </div>
                </div>

                {status === 'accepted' && !paid && (
                  <div className="bg-wheely-navy-900 rounded-lg p-6 text-center space-y-4">
                    <p className="text-gray-400">Pay now and your driver will set off straight away.</p>
                    <Button
                      onClick={handlePay}
                      className="bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700 text-white font-semibold"
                    >
                      Pay £{requestDetails?.final_price ?? estimatedCost}
                    </Button>
                  </div>
                )}

                {status === 'in_progress' && !cancellationRequested && (
                  <div className="bg-wheely-navy-900 rounded-lg p-6 text-center">
                    <p className="text-gray-400">Paid. Please be ready: your driver will call when they arrive.</p>
                  </div>
                )}

                {status === 'in_progress' && cancellationRequested && (
                  <div className="bg-wheely-navy-900 rounded-lg p-6 text-center space-y-2">
                    <p className="text-white font-semibold">You've asked to cancel</p>
                    <p className="text-gray-400">We've told the driver. We'll email you to confirm your refund.</p>
                  </div>
                )}
              </div>
            )}

            {!loading && status === 'completed' && (
              <div className="text-center space-y-4">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white">Service Completed</h3>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Service Details */}
        <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
          <CardHeader>
            <CardTitle className="text-white">Service Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Location</span>
              <span className="text-white text-right">{requestDetails?.location}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Vehicle</span>
              <span className="text-white">{requestDetails?.vehicle_model}{requestDetails?.vehicle_year ? ` (${requestDetails.vehicle_year})` : ''}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Issue</span>
              <span className="text-white text-right max-w-xs">{requestDetails?.issue_description}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-wheely-navy-700">
              <span className="text-white font-semibold">Estimated Cost</span>
              <span className="text-wheely-orange font-bold text-xl">£{requestDetails?.final_price ?? estimatedCost}</span>
            </div>
          </CardContent>
        </Card>

        {canCancel && (
          <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-navy-700">
            <CardContent className="pt-6 space-y-4">
              {!showCancel ? (
                <div className="text-center">
                  <Button variant="outline" onClick={() => setShowCancel(true)} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    Cancel this request
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <h4 className="text-white font-semibold">Cancel this request?</h4>
                  <p className="text-gray-400 text-sm">
                    {paid
                      ? "If the driver hasn't set off yet you'll get a full refund. If they have, you'll pay for the service provided up to now (for example their travel to you) and we'll refund the rest. We'll email you to confirm the amount."
                      : "You haven't paid yet, so cancelling is free."}
                  </p>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    maxLength={500}
                    rows={2}
                    placeholder="Reason (optional)"
                    className="w-full rounded-md bg-wheely-navy-900 border border-wheely-navy-700 text-white p-3 text-sm"
                  />
                  <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={() => setShowCancel(false)} disabled={cancelling}>
                      Keep my request
                    </Button>
                    <Button onClick={handleCancel} disabled={cancelling} className="bg-red-600 hover:bg-red-700 text-white">
                      {cancelling ? 'Cancelling...' : 'Yes, cancel'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ServiceTracking;
